import React, { useState } from 'react';
import { ImageIcon, SparklesIcon, TrashIcon } from './Icons';
import { stickerTextStyle } from './StickerText';
import { getSlideIntervalMs, reorderSlideshowAttachments } from '../utils/slideshowUtils';

export const isVideoUrl = (url: string) => {
    return url.match(/\.(mp4|webm|ogg)$/i) || (url.startsWith('blob:') && url.includes('#video'));
};

interface SlideshowBuilderProps {
    slideshow: any;
    onChangeSlideshow: (s: any) => void;
    nativeInputRef: React.RefObject<HTMLInputElement>;
    onNativeUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onOpenTenor?: () => void;
    onOpenPhotos?: (provider: 'apple' | 'google') => void;
    isAdmin?: boolean;
}

export const SlideshowBuilder: React.FC<SlideshowBuilderProps> = ({
    slideshow,
    onChangeSlideshow,
    nativeInputRef,
    onNativeUpload,
    onOpenTenor,
    onOpenPhotos,
    isAdmin
}) => {
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    const updateAttachment = (index: number, updates: Record<string, any>) => {
        const list = [...(slideshow.attachments || [])];
        list[index] = { ...list[index], ...updates };
        onChangeSlideshow({ ...slideshow, attachments: list });
    };

    const handleDrop = (targetIndex: number) => {
        if (draggedIndex === null || draggedIndex === targetIndex) return;
        const list = reorderSlideshowAttachments(slideshow.attachments || [], draggedIndex, targetIndex);
        onChangeSlideshow({ ...slideshow, attachments: list });
        setDraggedIndex(null);
    };

    const handleDelete = (index: number) => {
        const list = [...(slideshow.attachments || [])];
        list.splice(index, 1);
        onChangeSlideshow({ ...slideshow, attachments: list });
    };

    return (
        <div className="space-y-4 animate-fade-in">
            <div className="space-y-4 animate-fade-in">
                {/* Attachments List */}
                <div className="space-y-2">
                    <label className="text-[9px] font-bold uppercase tracking-wider text-white/60" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Slides ({slideshow.attachments?.length || 0})</label>
                    
                    {(!slideshow.attachments || slideshow.attachments.length === 0) ? (
                        <div className="p-6 text-center bg-black/10 border border-dashed border-white/10 rounded-2xl">
                            <p className="text-[10px] text-white/40 uppercase tracking-widest">No backgrounds added yet</p>
                        </div>
                    ) : (
                        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                            {slideshow.attachments.map((item: any, idx: number) => (
                                <div
                                    key={item.id || idx}
                                    draggable
                                    onDragStart={() => setDraggedIndex(idx)}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={() => handleDrop(idx)}
                                    onDragEnd={() => setDraggedIndex(null)}
                                    className="flex flex-col gap-2 p-2 bg-black/20 border border-white/5 rounded-xl"
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-10 h-10 rounded-lg overflow-hidden border border-white/10 shrink-0 bg-black/40">
                                                {item.type === 'video' || isVideoUrl(item.url) ? (
                                                    <video src={item.url} className="w-full h-full object-cover" muted playsInline />
                                                ) : (
                                                    <img src={item.url} className="w-full h-full object-cover" alt="" />
                                                )}
                                            </div>
                                            <span className="text-[10px] font-bold truncate text-white/80">{item.name || `Background Slide #${idx + 1}`}</span>
                                        </div>

                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <div className="p-1 rounded-lg bg-white/5 text-white/60 cursor-grab active:cursor-grabbing">
                                                <span className="text-[10px] block leading-none">⋮⋮</span>
                                            </div>
                                            <button
                                                onClick={() => handleDelete(idx)}
                                                className="p-1 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-all"
                                            >
                                                <TrashIcon className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid gap-2 md:grid-cols-[1fr_auto] items-center">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[9px] font-bold uppercase tracking-tight text-white/60">Fill / Tile</span>
                                            <div className="flex bg-black/40 p-1 rounded-xl border border-white/5">
                                                {(['cover', 'tile'] as const).map(style => (
                                                    <button
                                                        key={style}
                                                        type="button"
                                                        onClick={() => updateAttachment(idx, { style })}
                                                        className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase transition-all ${item.style === style ? 'bg-white/20 text-white' : 'text-white/70'}`}
                                                    >
                                                        {style === 'cover' ? 'Fill' : 'Tile'}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <span className="text-[9px] font-bold uppercase tracking-tight text-white/60">Timer</span>
                                            <input
                                                type="number"
                                                min="1"
                                                value={item.durationValue || slideshow.intervalValue || 10}
                                                onChange={(e) => updateAttachment(idx, { durationValue: Math.max(1, Number(e.target.value)) })}
                                                className="w-12 bg-black/40 border border-white/10 rounded-lg text-center text-[10px] py-1 text-white"
                                            />
                                            <select
                                                value={item.durationUnit || slideshow.intervalUnit || 'seconds'}
                                                onChange={(e) => updateAttachment(idx, { durationUnit: e.target.value })}
                                                className="bg-black/40 border border-white/10 rounded-lg text-[9px] font-bold py-1 px-1.5 text-white uppercase"
                                            >
                                                <option value="seconds">Sec</option>
                                                <option value="minutes">Min</option>
                                                <option value="hours">Hrs</option>
                                                <option value="days">Days</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Add Media Options */}
                <div className="grid grid-cols-2 gap-2">
                    <button
                        onClick={() => nativeInputRef.current?.click()}
                        className="flex flex-col items-center justify-center p-3 bg-black/20 hover:bg-black/40 border border-white/5 rounded-2xl transition-all gap-1.5 group"
                    >
                        <ImageIcon className="w-4 h-4 text-blue-400 group-hover:scale-110 transition-transform" />
                        <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">Upload Files</span>
                    </button>

                    {onOpenTenor && (
                        <button
                            onClick={onOpenTenor}
                            className="flex flex-col items-center justify-center p-3 bg-black/20 hover:bg-black/40 border border-white/5 rounded-2xl transition-all gap-1.5 group"
                        >
                            <SparklesIcon className="w-4 h-4 text-purple-400 group-hover:scale-110 transition-transform" />
                            <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">Tenor GIF</span>
                        </button>
                    )}

                    {isAdmin && onOpenPhotos && (
                        <>
                            <button
                                onClick={() => onOpenPhotos('apple')}
                                className="flex flex-col items-center justify-center p-3 bg-black/20 hover:bg-black/40 border border-white/5 rounded-2xl transition-all gap-1.5 group"
                            >
                                <span className="text-base text-gray-300 font-bold group-hover:scale-110 transition-transform">?</span>
                                <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">Apple Photos</span>
                            </button>

                            <button
                                onClick={() => onOpenPhotos('google')}
                                className="flex flex-col items-center justify-center p-3 bg-black/20 hover:bg-black/40 border border-white/5 rounded-2xl transition-all gap-1.5 group"
                            >
                                <span className="text-xs text-yellow-500 font-bold group-hover:scale-110 transition-transform">G</span>
                                <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">Google Photos</span>
                            </button>
                        </>
                    )}
                </div>

                {/* Hidden Native File Input */}
                <input
                    type="file"
                    ref={nativeInputRef}
                    onChange={onNativeUpload}
                    multiple
                    accept="image/*,video/*"
                    className="hidden"
                />

                {/* Interval controls */}
                <div className="flex items-center justify-between p-3 bg-black/20 border border-white/5 rounded-2xl gap-3">
                    <span className="text-[10px] font-bold uppercase tracking-tight text-white/80" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Default Rotate Every</span>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min="1"
                            value={slideshow.intervalValue || 10}
                            onChange={(e) => onChangeSlideshow({ ...slideshow, intervalValue: Math.max(1, Number(e.target.value)) })}
                            className="w-12 bg-black/40 border border-white/10 focus:border-white/30 rounded-xl text-center text-xs py-1 text-white"
                        />
                        <select
                            value={slideshow.intervalUnit || 'seconds'}
                            onChange={(e: any) => onChangeSlideshow({ ...slideshow, intervalUnit: e.target.value })}
                            className="bg-black/40 border border-white/10 focus:border-white/30 rounded-xl text-[10px] font-bold py-1 px-1.5 text-white uppercase"
                        >
                            <option value="seconds">Sec</option>
                            <option value="minutes">Min</option>
                            <option value="hours">Hrs</option>
                            <option value="days">Days</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};
